/**
 * Iter 67en — Admin FAQ manager.
 *
 * Sister tile to AdminChatAnalyticsTile. Lists every FAQ row (drafts +
 * published), lets admin publish / unpublish, edit any locale's
 * translation in-place, and delete. Drafts created by the "Promote to
 * FAQ" button on the analytics tile land here for review.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Eye, EyeOff, Trash2, Save, ChevronDown, ChevronUp, Globe2, Sparkles, Loader2 } from "lucide-react";

const LOCALE_NAMES = {
  en: "English", sq: "Albanian", fr: "French", de: "German", it: "Italian",
  es: "Spanish", pt: "Portuguese", pl: "Polish", ro: "Romanian", el: "Greek",
  bg: "Bulgarian", tr: "Turkish", ru: "Russian",
};

export default function AdminFaqsPanel() {
  const [data, setData] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [drafts, setDrafts] = useState({}); // {id: {answers, question}}
  const [busy, setBusy] = useState({}); // {id: "retranslate"} | {bulk: true}

  const load = async () => {
    try {
      const { data: res } = await api.get("/admin/chat-faqs");
      setData(res);
    } catch {
      setData({ faqs: [], supported_locales: [] });
    }
  };

  useEffect(() => { load(); }, []);

  const togglePublish = async (faq) => {
    try {
      await api.patch(`/admin/chat-faqs/${faq.id}`, { is_published: !faq.is_published });
      toast.success(faq.is_published ? "FAQ unpublished." : "FAQ now live on /faq.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    }
  };

  const remove = async (faq) => {
    if (!window.confirm(`Delete "${faq.question}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/chat-faqs/${faq.id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  const saveEdits = async (faq) => {
    const d = drafts[faq.id];
    if (!d) return;
    try {
      await api.patch(`/admin/chat-faqs/${faq.id}`, {
        question: d.question ?? undefined,
        answers: d.answers ?? undefined,
      });
      toast.success("Translations saved.");
      setDrafts((s) => ({ ...s, [faq.id]: undefined }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  /**
   * Iter 67en++++ — Re-translate one FAQ row from the current English copy.
   * Backend regenerates the 12 non-English locales via the same LLM
   * pipeline that runs on initial promotion. Saved drafts on the row are
   * cleared so the new server-side translations don't get clobbered.
   */
  const retranslateOne = async (faq) => {
    setBusy((s) => ({ ...s, [faq.id]: "retranslate" }));
    try {
      const { data: res } = await api.post("/admin/chat-faqs/retranslate", { faq_id: faq.id });
      if (res.processed > 0) {
        toast.success("Re-translated into 12 languages.");
        setDrafts((s) => ({ ...s, [faq.id]: undefined }));
        load();
      } else {
        toast.error("Re-translate skipped — English copy is empty?");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-translate failed.");
    } finally {
      setBusy((s) => ({ ...s, [faq.id]: undefined }));
    }
  };

  /**
   * Bulk re-translate every FAQ. Hits the same endpoint with no `faq_id`
   * so the backend iterates all rows. Confirmation gate because each row
   * burns ~1 LLM call and could be costly on a 50-row library.
   */
  const retranslateAll = async () => {
    const total = (data?.faqs || []).length;
    if (!total) return;
    if (!window.confirm(`Re-translate all ${total} FAQs from their English copy? This will overwrite every non-English translation.`)) return;
    setBusy((s) => ({ ...s, bulk: true }));
    try {
      const { data: res } = await api.post("/admin/chat-faqs/retranslate", { faq_id: null });
      toast.success(`Re-translated ${res.processed}/${res.total_matched} FAQs.${res.failed?.length ? ` ${res.failed.length} skipped (empty English).` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk re-translate failed.");
    } finally {
      setBusy((s) => ({ ...s, bulk: false }));
    }
  };

  if (!data) {
    return <div className="rounded-2xl border border-border bg-white p-5 text-sm text-neutral-500">Loading FAQs…</div>;
  }

  const faqs = data.faqs || [];
  const locales = data.supported_locales || [];

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="admin-faqs-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center">
            <BookOpen className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-base font-semibold">FAQ library</div>
            <div className="text-xs text-neutral-500">{faqs.length} entries · drafts visible only to admins</div>
          </div>
        </div>
        {faqs.length > 0 && (
          <button
            type="button"
            onClick={retranslateAll}
            disabled={busy.bulk}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200 text-xs font-semibold transition disabled:opacity-60 disabled:cursor-not-allowed"
            title="Re-run the LLM translation for every FAQ from its current English answer. Useful after editing English copy."
            data-testid="admin-faqs-bulk-retranslate"
          >
            {busy.bulk ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {busy.bulk ? "Re-translating…" : "Bulk re-translate"}
          </button>
        )}
      </div>

      {faqs.length === 0 ? (
        <div className="text-sm text-neutral-500 italic py-6 text-center">
          No FAQs yet. Use "Promote to FAQ" on a top question in the analytics tile to draft your first one.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-faqs-list">
          {faqs.map((f) => {
            const expanded = expandedId === f.id;
            const draft = drafts[f.id] || { answers: { ...(f.answers || {}) }, question: f.question };
            return (
              <li key={f.id} className="rounded-xl border border-neutral-200 bg-white">
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : f.id)}
                    className="flex-1 text-left flex items-center gap-2 min-w-0"
                    data-testid={`admin-faq-row-${f.id}`}
                  >
                    {expanded ? <ChevronUp className="h-3.5 w-3.5 text-neutral-400 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-neutral-400 shrink-0" />}
                    <span className="text-sm font-medium text-neutral-900 truncate">{f.question}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold tabular-nums text-amber-600 shrink-0">{f.popularity_count}×</span>
                  </button>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full shrink-0 ${f.is_published ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {f.is_published ? "PUBLISHED" : "DRAFT"}
                  </span>
                  <button
                    type="button"
                    onClick={() => togglePublish(f)}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-neutral-100 hover:bg-neutral-200 text-neutral-700 transition"
                    data-testid={`admin-faq-toggle-publish-${f.id}`}
                  >
                    {f.is_published ? <><EyeOff className="h-3 w-3" /> Unpublish</> : <><Eye className="h-3 w-3" /> Publish</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(f)}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 transition"
                    title="Delete"
                    data-testid={`admin-faq-delete-${f.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
                {expanded && (
                  <div className="border-t border-neutral-200 px-3 py-3 space-y-3 bg-neutral-50/60">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Question</label>
                      <input
                        type="text"
                        value={draft.question}
                        onChange={(e) => setDrafts((s) => ({ ...s, [f.id]: { ...draft, question: e.target.value } }))}
                        className="w-full mt-1 px-2 py-1.5 rounded-lg border border-neutral-200 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {locales.map((loc) => (
                        <div key={loc}>
                          <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 inline-flex items-center gap-1">
                            <Globe2 className="h-3 w-3" /> {LOCALE_NAMES[loc] || loc} · {loc.toUpperCase()}
                          </label>
                          <textarea
                            rows={4}
                            value={draft.answers[loc] || ""}
                            onChange={(e) => setDrafts((s) => ({
                              ...s,
                              [f.id]: { ...draft, answers: { ...draft.answers, [loc]: e.target.value } },
                            }))}
                            className="w-full mt-1 px-2 py-1.5 rounded-lg border border-neutral-200 text-xs font-mono bg-white"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => retranslateOne(f)}
                        disabled={busy[f.id] === "retranslate"}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200 text-xs font-semibold transition disabled:opacity-60"
                        title="Re-run the LLM translation for this FAQ from its current English answer."
                        data-testid={`admin-faq-retranslate-${f.id}`}
                      >
                        {busy[f.id] === "retranslate" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        {busy[f.id] === "retranslate" ? "Re-translating…" : "Re-translate from English"}
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdits(f)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold transition"
                        data-testid={`admin-faq-save-${f.id}`}
                      >
                        <Save className="h-3 w-3" /> Save translations
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
